import { Button } from '@revenuemarket/cds';
import { colors, thematicColors } from '@revenuemarket/design';
import { DenialType } from '@revenuemarket/trade';
import { Typography } from '@revenuemarket/ui';
import { ArrowFront, ArrowNorthEast, InfoCircleIcon } from '@revenuemarket/ui/Icon';
import { makeHourText } from '@revenuemarket/utils';
import { fixedNumFormat } from '@revenuemarket/utils/number/formatNum';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { RecentTermResponse } from '~apis/term-request/getRecentTermRequest';
import { PAGE_STATUS } from '~pages/dashboard/term-request';

export const getDetailedDeniedDescription = (
  denialType: DenialType,
  expectedPayout: number | null,
) => {
  switch (denialType) {
    case 'NEED_REQUIREMENTS':
      return {
        title: '아쉽지만 지금은 매출을 판매할 수 없습니다.',
        description: '최소 이용 조건을 충족하신 다음 새로운 양도 조건을 요청해주세요.',
        subDescription: '클로브 금융 최소 이용 요건에 해당하지 않았습니다.',
        link: {
          text: '더 알아보기',
          href: 'https://intercom.help/clobe/ko/articles/8847464-%EB%A0%88%EB%B2%A0%EB%89%B4%EB%A7%88%EC%BC%93-%EC%9D%B4%EC%9A%A9-%EB%B0%8F-%ED%8C%90%EB%A7%A4-%EC%A1%B0%EA%B1%B4-%EC%95%88%EB%82%B4',
        },
      };
    case 'NO_TRADABLE_REVENUE':
      return {
        title: '아쉽지만 지금은 판매할 수  있는 매출 계약이 없습니다.',
        description:
          '연결해주신 매출 외 다른 매출이 있다면 매출 업로드 후 새로운 양도 조건을 요청해주세요.',
        subDescription:
          '월별 반복 매출이 없으시거나 매출처가 다양하지 않은 경우, 또는 매출의 종류가 오프라인 카드 가맹점일 경우 판매가 불가합니다.',
        link: {
          text: '매출 업로드',
          href: '',
        },
      };
    case 'SHORT_RUNWAY':
      const payout = ((expectedPayout || 0) / 100_000_000).toFixed(1);
      return {
        title: '아쉽지만 지금은 매출을 판매할 수 없습니다.',
        description: `3개월 이상 런웨이를 확보하면 회수 기간 3개월, 약 ${payout}억 원의 양도대금을 신청할 수 있을 것으로 예상됩니다.\n추후 런웨이가 개선되면 새로운 양도 조건을 요청해주세요.`,
        subDescription:
          '지금은 클로브 금융 최소 이용 조건 중 런웨이 3개월 이상 조건에 해당하지 않았습니다.',
      };
    case 'UC_FAIL':
      return {
        title: '아쉽지만 지금은 매출을 판매할 수 없습니다.',
        description:
          '기업의 성장을 반영하기 위해 최근 12개월 데이터를 기준으로 분석합니다. 다음 달에 매출 및 재무상황이 개선된다면 다시 한번 한도 조회를 요청해주세요.',
        subDescription:
          '클로브는 매출의 월별 반복성·성장성, 월별 비용 추이, 부채비율 및 여러 지표를 종합하여 양도 조건을 분석하고 있습니다.',
        link: {
          text: '더 알아보기',
          href: 'https://intercom.help/clobe/ko/articles/8847464-%EB%A0%88%EB%B2%A0%EB%89%B4%EB%A7%88%EC%BC%93-%EC%9D%B4%EC%9A%A9-%EB%B0%8F-%ED%8C%90%EB%A7%A4-%EC%A1%B0%EA%B1%B4-%EC%95%88%EB%82%B4',
        },
      };
    case 'BELOW_MINIMUM_LIMIT':
      return {
        title: '아쉽지만 지금은 매출을 판매할 수 없습니다.',
        description:
          '기업의 빠른 성장을 반영하기 위해 최근 12개월 데이터를 기준으로 분석하므로, 다음 달에 매출 및 재무 지표가 개선된다면 새로운 양도 조건을 요청해주세요.',
        subDescription:
          '클로브 금융 최소 한도에 해당하지 않았습니다. 클로브 금융은 현재 최소 5,000만원 이상의 한도가 발생했을 때 자금을 제공해드리고 있습니다.',
        link: {
          text: '더 알아보기',
          href: 'https://intercom.help/clobe/ko/articles/8847464-%EB%A0%88%EB%B2%A0%EB%89%B4%EB%A7%88%EC%BC%93-%EC%9D%B4%EC%9A%A9-%EB%B0%8F-%ED%8C%90%EB%A7%A4-%EC%A1%B0%EA%B1%B4-%EC%95%88%EB%82%B4',
        },
      };
    default:
      break;
  }
};

export const renderResult = (
  status: PAGE_STATUS,
  termRequestData: RecentTermResponse,
  setIsEditMode?: () => void,
  needCeoCreditAgreement?: boolean,
) => {
  const offeredAtHourText = makeHourText(termRequestData.result?.offeredAt);
  const expiredAtHourText = makeHourText(termRequestData.result?.expiredAt);

  switch (status) {
    case 'BEFORE_ANALYSIS':
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Typography color={colors.grayscale200} variant="body1">
              {needCeoCreditAgreement ? (
                <>
                  입력하신 데이터를 확인하고 있습니다.
                  <br />
                  데이터 확인 및 대표자의 신용 조회 동의가 완료된 이후, 2 영업일 이내에 결과를
                  알려드립니다.
                </>
              ) : (
                <>
                  입력하신 데이터를 확인하고 있습니다. <br />
                  데이터 확인 이후, 2 영업일 이내에 결과를 알려드립니다.
                </>
              )}
            </Typography>
          </div>
          <Button buttonType="default" buttonSize="small" onClick={setIsEditMode}>
            입력한 정보 수정
          </Button>
        </div>
      );

    case 'AFTER_ANALYSIS':
      const hourText = makeHourText(termRequestData?.analysisFinishedUntil);
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography color={colors.grayscale200} variant="body1">
              양도 조건을 분석하는 중입니다.
            </Typography>
            <Typography color={colors.grayscale200} variant="body1">
              {`${dayjs(termRequestData?.analysisFinishedUntil).format(
                `YYYY년 MM월 DD일 ${hourText} hh:mm`,
              )}에 분석 결과를 알려드릴게요.`}
            </Typography>
          </div>
        </div>
      );
    case 'OFFERED':
      const router = useRouter();

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography color={colors.grayscale400} variant="body2">
            {`${dayjs(termRequestData.result.offeredAt).format(
              `MM월 DD일 ${offeredAtHourText} hh:MM`,
            )} ~ ${dayjs(termRequestData.result.expiredAt).format(
              `MM월 DD일 ${expiredAtHourText} hh:MM`,
            )}`}
          </Typography>
          <div
            style={{
              padding: '16px 0px',
              borderBottom: `1px solid ${colors.grayscale600}`,
              width: 280,
            }}
            id="term-approve"
          >
            <Typography color={colors.grayscaleWhite} variant="h3" style={{ textAlign: 'center' }}>
              판매 가능
            </Typography>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 24,
              padding: '16px 0px',
              borderBottom: `1px solid ${colors.grayscale600}`,
              width: 280,
            }}
          >
            <Typography
              color={colors.grayscaleWhite}
              variant="body1"
              style={{ textAlign: 'right', width: '50%' }}
            >
              신청 기한
            </Typography>
            <Typography
              color={colors.grayscaleWhite}
              variant="body1"
              style={{ textAlign: 'left', width: '50%' }}
            >
              {`${dayjs(termRequestData.result.expiredAt).format('YYYY-MM-DD')}`}
            </Typography>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 24,
              padding: '16px 0px',
              borderBottom: `1px solid ${colors.grayscale600}`,
              width: 280,
            }}
          >
            <Typography
              color={colors.grayscaleWhite}
              variant="body1"
              style={{ textAlign: 'right', width: '50%' }}
            >
              회수기간
            </Typography>
            <Typography
              color={colors.grayscaleWhite}
              variant="body1"
              style={{ textAlign: 'left', width: '50%' }}
            >
              {`${termRequestData?.result.tradingPeriod}개월`}
            </Typography>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 24,
              padding: '16px 0px',
              borderBottom: `1px solid ${colors.grayscale600}`,
              width: 280,
            }}
          >
            <Typography
              color={colors.grayscaleWhite}
              variant="body1"
              style={{ textAlign: 'right', width: '50%' }}
            >
              할인율
            </Typography>
            <Typography
              color={colors.grayscaleWhite}
              variant="body1"
              style={{ textAlign: 'left', width: '50%' }}
            >
              {`${termRequestData.result.tradingDiscountRate * 100}%`}
            </Typography>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 24,
              padding: '16px 0px',
              borderBottom: `1px solid ${colors.grayscale600}`,
              width: 280,
            }}
          >
            <Typography
              color={colors.grayscaleWhite}
              variant="body1"
              style={{ textAlign: 'right', width: '50%' }}
            >
              한도
            </Typography>
            <Typography
              color={colors.grayscaleWhite}
              variant="body1"
              style={{ textAlign: 'left', width: '50%' }}
            >
              {`${fixedNumFormat(termRequestData.result.tradingPayoutLimit, 0, {
                suffix: '원',
                addComma: true,
              })}`}
            </Typography>
          </div>

          <Button
            buttonType="primary"
            buttonSize="large"
            style={{
              marginTop: 16,
              width: 280,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => {
              router.push('/dashboard/trade-revenue');
            }}
          >
            조건대로 장래매출채권 양도하기
            <ArrowFront fill={colors.grayscale850} width={16} height={16} />
          </Button>
        </div>
      );
    case 'DENIED':
      const denialContent = getDetailedDeniedDescription(
        termRequestData.result.termOfferDenialReason,
        termRequestData.result.expectedPayoutLimit,
      );
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {' '}
          <Typography color={colors.grayscale400} variant="body2">
            {`${dayjs(termRequestData.result.offeredAt).format(
              `MM월 DD일 ${offeredAtHourText} hh:MM`,
            )} ~ ${dayjs(termRequestData.result.expiredAt).format(
              `MM월 DD일 ${expiredAtHourText} hh:MM`,
            )}`}
          </Typography>
          <Typography
            variant="h3"
            color={colors.grayscale200}
            style={{ marginTop: 16 }}
            id="denial-title"
          >
            {denialContent?.title}
          </Typography>
          {denialContent?.subDescription && (
            <Typography variant="body1" color={colors.grayscale200} style={{ marginTop: 8 }}>
              {denialContent.subDescription}
            </Typography>
          )}
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <InfoCircleIcon fill={thematicColors.secondary.regular} width={16} height={16} />
              <Typography variant="h4" color={colors.grayscale200}>
                매출을 판매하려면?
              </Typography>
            </div>
          </div>
          <Typography
            variant="body1"
            color={colors.grayscale200}
            style={{
              whiteSpace: 'pre-line',
              marginTop: 8,
            }}
            id="denial-description"
          >
            {denialContent?.description}
          </Typography>
          {denialContent?.link && (
            <div style={{ display: 'flex', marginTop: 2 }}>
              <a
                href={denialContent.link.href}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'flex',
                }}
              >
                <Typography
                  variant="body1"
                  color={thematicColors.secondary.regular}
                  style={{ textDecoration: 'underline' }}
                >
                  {denialContent.link.text}
                </Typography>
                <ArrowNorthEast fill={thematicColors.secondary.regular} width={18} height={18} />
              </a>
            </div>
          )}
        </div>
      );
    default:
      return <div />;
  }
};
